import { PlusIcon } from "@phosphor-icons/react/Plus";
import { useSavedBrokersApi } from "@/Hooks/useSavedBrokersApi";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import PageTitle from "@/Shared/Layout/PageTitle";
import type { UUID } from "@/Shared/types";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { PreferredBrokerType } from "@freightsimple/generated-dashboard-openapi-client/models/PreferredBrokerType";
import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import { useOnce } from "@/Hooks/useOnce";
import { useState } from "react";
import BrokersBox from "@/Shared/Brokers/CustomComponents/BrokersBox";
import { useBrokersAddBrokerDialog } from "@/Features/Brokers/Exports/Hooks/useBrokersAddBrokerDialog";
import { useBrokersModifyBrokerDialog } from "@/Features/Brokers/Exports/Hooks/useBrokersModifyBrokerDialog";
import { useSavedBrokersService } from "./Services/BrokersSavedService";

const BrokersScreen: React.FC = () => {
  const savedBrokersService = useSavedBrokersService();

  const [loading, setLoading] = useState(false);

  const [brokers, setBrokers] = useState<undefined | Array<SavedBroker>>();
  const savedBrokersApi = useSavedBrokersApi();
  const [preferredNorthboundId, setPreferredNorthboundId] = useState<
    UUID | undefined
  >(undefined);
  const [preferredSouthboundId, setPreferredSouthboundId] = useState<
    UUID | undefined
  >(undefined);
  const [pendingDeleteId, setPendingDeleteId] = useState<UUID | undefined>(
    undefined,
  );

  async function loadSavedBrokers() {
    setLoading(true);
    const savedBrokersResponse = await savedBrokersApi.getAllSavedBrokers();
    setBrokers(
      savedBrokersResponse.items.sort((a, b) => {
        return a.broker.businessName.localeCompare(b.broker.businessName);
      }),
    );
    setPreferredNorthboundId(savedBrokersResponse.preferredNorthboundBrokerId);
    setPreferredSouthboundId(savedBrokersResponse.preferredSouthboundBrokerId);

    setLoading(false);
  }

  useOnce(loadSavedBrokers);

  async function onModifyBroker(brokerId: UUID, updatedBroker: Broker) {
    await savedBrokersService.modifyBroker(brokerId, updatedBroker);
    await loadSavedBrokers();
  }

  const addBrokerDialog = useBrokersAddBrokerDialog();

  async function onAddBroker() {
    const newSavedBroker = await addBrokerDialog.show();
    if (newSavedBroker !== undefined) {
      await savedBrokersService.addBroker(newSavedBroker);
      await loadSavedBrokers();
    }
  }

  const modifyBrokerDialog = useBrokersModifyBrokerDialog();

  async function onConfirmedDelete() {
    if (pendingDeleteId === undefined) {
      return;
    }
    await savedBrokersService.deleteSavedBroker(pendingDeleteId);
    await loadSavedBrokers();
  }

  async function onSelectPreferred(
    savedBrokerId: UUID,
    preferredBrokerType: PreferredBrokerType,
  ) {
    await savedBrokersService.setPreferred(savedBrokerId, preferredBrokerType);
    await loadSavedBrokers();
  }

  return (
    <>
      <PageTitle>Customs Brokers</PageTitle>
      {/* Dialogs live outside the layout so they work from the empty state */}
      {addBrokerDialog.dialogs}
      {modifyBrokerDialog.dialogs}
      <Dialog
        open={pendingDeleteId !== undefined}
        onOpenChange={function (nowOpen) {
          if (!nowOpen) {
            setPendingDeleteId(undefined);
          }
        }}
        title="Confirm delete"
        okText="Delete Broker"
        onOk={onConfirmedDelete}
      >
        <ControlPresentationProvider presentation="underlined">
          Are you sure you want to delete this customs broker?
        </ControlPresentationProvider>
      </Dialog>
      <FullWidthLayout
        header=" Customs Brokers"
        rightContent={
          <Button
            primary
            icon={PlusIcon}
            onClick={onAddBroker}
            data-testid="add-new-customs-broker"
          >
            Add New Customs Broker
          </Button>
        }
        microcopy="Save brokerage info here to speed up booking future shipments."
        loading={loading}
        showEmptyState={brokers?.length === 0}
        emptyState={
          <Stack align="center" gap={8}>
            <Stack align="center" gap={0}>
              {/* Replica of the legacy Cargo Heading1 (28px bold). */}
              <Heading level={1}>You have no saved customs brokers</Heading>
              <T size="base" color="secondary">
                Save customs brokerage info here to speed up booking future
                shipments.
              </T>
            </Stack>
            <Button primary size="large" icon={PlusIcon} onClick={onAddBroker}>
              Add New Customs Broker
            </Button>
          </Stack>
        }
        content={
          <Stack gap={8}>
            {brokers?.map((item) => (
              <BrokersBox
                key={item.savedBrokerId}
                broker={item.broker}
                onModify={async function () {
                  const result = await modifyBrokerDialog.show(item);
                  if (result !== undefined) {
                    onModifyBroker(result.savedBrokerId, result.broker);
                  }
                }}
                showMenu={true}
                onDelete={function () {
                  setPendingDeleteId(item.savedBrokerId);
                }}
                preferredNorthbound={
                  preferredNorthboundId === item.savedBrokerId
                }
                preferredSouthbound={
                  preferredSouthboundId === item.savedBrokerId
                }
                onSelectPreferred={function (
                  preferredBrokerType: PreferredBrokerType,
                ) {
                  onSelectPreferred(item.savedBrokerId, preferredBrokerType);
                }}
              />
            ))}
          </Stack>
        }
      />
    </>
  );
};

export default BrokersScreen;
